// Chia tổng đơn lịch hẹn thành nhiều đợt (trả góp, API POS V2.1): thu đợt 1 ngay, kế hoạch đợt gửi lên server (installments[])
import React, { useMemo, useRef, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/vi';
import { Calendar, Checkbox, ConfigProvider, Popover, Select } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { CalendarClock, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { PaymentLineInput } from '../data';
import MoneyInput from './MoneyInput';

export interface PlanRow {
  /** YYYY-MM-DD */
  dueDate: string;
  amount: number;
}

const UNIT = 1000;
/** Gợi ý sẵn trong dropdown; gõ số để chọn tới MAX_INSTALLMENTS đợt */
const PRESET_COUNTS = [2, 3, 4, 5, 6];
// Quy tắc trả góp của server (API POS V2.1): tối đa 12 đợt, đợt cuối trong 12 tháng, đợt 1 ≥ 30% (thấp hơn cần Quản lý duyệt)
export const MAX_INSTALLMENTS = 12;
export const MAX_PLAN_MONTHS = 12;
export const MIN_FIRST_PCT = 30;
const DATE_FMT = 'DD/MM/YYYY';
const ISO = 'YYYY-MM-DD';
const WEEKDAY = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

// ---------------------------------------------------------------- kỳ hạn

interface Cycle { n: number; unit: 'week' | 'month'; label: string }
const CYCLES: Cycle[] = [
  { n: 2, unit: 'week', label: '2 tuần' },
  { n: 1, unit: 'month', label: '1 tháng' },
  { n: 2, unit: 'month', label: '2 tháng' },
  { n: 3, unit: 'month', label: '3 tháng' },
];
const DEFAULT_CYCLE = CYCLES[1];

const datesFor = (from: Dayjs, count: number, c: Cycle) => Array.from({ length: count }, (_, i) => from.add(i * c.n, c.unit).format(ISO));
/** Lịch đang theo đúng một kỳ hạn mẫu (cùng ngày mỗi tháng / mỗi 2 tuần…) thì trả về kỳ đó */
const detectCycle = (rows: PlanRow[]) => {
  const from = dayjs(rows[0].dueDate);
  return CYCLES.find(c => rows.every((r, i) => r.dueDate === from.add(i * c.n, c.unit).format(ISO))) || null;
};

/** Khoảng cách giữa hai hạn: tròn tháng lịch thì tính theo tháng, còn lại theo ngày */
const gapOf = (prev: Dayjs, cur: Dayjs): { k: number; unit: 'month' | 'day' } => {
  for (let k = 1; k <= 24; k += 1) if (prev.add(k, 'month').isSame(cur, 'day')) return { k, unit: 'month' };
  return { k: cur.diff(prev, 'day'), unit: 'day' };
};

/** Khoảng cách so với đợt trước: +1 tháng, +2 tuần, +45 ngày */
const relLabel = (prev: Dayjs, cur: Dayjs) => {
  const { k, unit } = gapOf(prev, cur);
  if (unit === 'month') return `+${k} tháng`;
  return k % 7 === 0 ? `+${k / 7} tuần` : `+${k} ngày`;
};

const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/\s+/g, '');

/** Ngày dd/mm/yyyy hợp lệ (không tràn sang tháng sau) */
const makeDate = (d: number, m: number, y: number) => {
  const v = dayjs(new Date(y, m - 1, d));
  return v.date() === d && v.month() === m - 1 ? v : null;
};

/**
 * Gõ tắt hạn đợt, tính từ hạn đợt trước:
 * "15" → ngày 15 kế tiếp · "15/12" · "15/12/27" · "151227" · "+45" (ngày) · "+2t" (tháng) · "+3w" / "3 tuần"
 */
export const parseDueInput = (raw: string, prev: Dayjs): Dayjs | string | null => {
  const s = fold(raw);
  if (!s) return null;
  let d: Dayjs | null = null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^\+?(\d{1,2})(t|th|thang|m)$/))) d = prev.add(+m[1], 'month');
  else if ((m = s.match(/^\+?(\d{1,2})(w|tuan)$/))) d = prev.add(+m[1] * 7, 'day');
  else if ((m = s.match(/^\+(\d{1,4})(d|n|ngay)?$/)) || (m = s.match(/^(\d{1,4})(d|n|ngay)$/))) d = prev.add(+m[1], 'day');
  else if ((m = s.match(/^(\d{1,2})$/))) {
    // Chỉ gõ ngày: lấy ngày đó ở tháng gần nhất sau đợt trước
    const day = +m[1];
    for (let k = 0; k <= 12 && !d; k += 1) {
      const month = prev.add(k, 'month');
      const cand = day <= month.daysInMonth() ? month.date(day) : null;
      if (cand && cand.isAfter(prev, 'day')) d = cand;
    }
  } else if ((m = s.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/)) || (m = s.match(/^(\d{2})(\d{2})(\d{2}|\d{4})$/))) {
    const [day, month] = [+m[1], +m[2]];
    if (m[3]) d = makeDate(day, month, m[3].length === 2 ? 2000 + +m[3] : +m[3]);
    else {
      // Không gõ năm: năm gần nhất để ngày nằm sau đợt trước
      d = makeDate(day, month, prev.year());
      if (d && !d.isAfter(prev, 'day')) d = makeDate(day, month, prev.year() + 1);
    }
    if (!d) return 'Ngày không hợp lệ';
  } else return 'Không hiểu – thử 15/12, +45, +2t';
  if (!d) return 'Ngày không hợp lệ';
  return d.isAfter(prev, 'day') ? d : `Phải sau đợt trước (${prev.format(DATE_FMT)})`;
};

// ---------------------------------------------------------------- tính toán lịch

/**
 * Chia đều tổng (làm tròn nghìn), đợt cuối nhận phần lẻ; đợt 1 là hôm nay, các đợt sau theo kỳ hạn (mặc định cùng ngày mỗi tháng).
 * Đợt 1 không thấp hơn 30% để khỏi cần duyệt – các đợt sau chia đều phần còn lại.
 */
export const splitPlan = (total: number, count: number, from: Dayjs = dayjs(), cycle: Cycle = DEFAULT_CYCLE): PlanRow[] => {
  const even = Math.floor(total / count / UNIT) * UNIT;
  const first = Math.min(total, Math.max(even, Math.ceil((total * MIN_FIRST_PCT) / 100 / UNIT) * UNIT));
  const each = count > 1 ? Math.floor((total - first) / (count - 1) / UNIT) * UNIT : 0;
  return datesFor(from, count, cycle).map((dueDate, i) => ({
    dueDate,
    amount: i === 0 ? first : i === count - 1 ? total - first - each * (count - 2) : each,
  }));
};

/** % đợt 1 trên tổng đơn */
export const firstPercent = (rows: PlanRow[], total: number) => (total > 0 ? (rows[0].amount / total) * 100 : 100);
/** Đợt 1 < 30% → server đòi plan_approved_by (Quản lý) */
export const planNeedsApproval = (rows: PlanRow[], total: number) => firstPercent(rows, total) < MIN_FIRST_PCT - 1e-9;
/** Hạn chót cho đợt cuối (12 tháng kể từ đợt 1) */
export const planMaxDate = (rows: PlanRow[]) => dayjs(rows[0].dueDate).add(MAX_PLAN_MONTHS, 'month');
/** Kế hoạch gửi API: đợt 1 luôn là hôm nay */
export const planToApi = (rows: PlanRow[]) =>
  rows.map((r, i) => ({ due_date: i === 0 ? dayjs().format(ISO) : r.dueDate, amount: r.amount }));

/** Tổng đơn đổi (sửa giỏ, CK…) → giữ tỷ lệ các đợt, đợt cuối nhận phần lẻ */
export const rescalePlan = (rows: PlanRow[], total: number): PlanRow[] => {
  const sum = rows.reduce((s, r) => s + r.amount, 0);
  if (sum <= 0) return splitPlan(total, rows.length);
  let used = 0;
  return rows.map((r, i) => {
    if (i === rows.length - 1) return { ...r, amount: total - used };
    const amount = Math.round((r.amount / sum) * total / UNIT) * UNIT;
    used += amount;
    return { ...r, amount };
  });
};

export const planError = (rows: PlanRow[], total: number): string | null => {
  if (rows.length > MAX_INSTALLMENTS) return `Tối đa ${MAX_INSTALLMENTS} đợt`;
  const i = rows.findIndex(r => r.amount <= 0);
  if (i > -1) return `Đợt ${i + 1} phải lớn hơn 0`;
  if (rows.reduce((s, r) => s + r.amount, 0) !== total) return 'Tổng các đợt chưa khớp tổng đơn';
  const d = rows.findIndex((r, j) => j > 0 && !dayjs(r.dueDate).isAfter(rows[j - 1].dueDate, 'day'));
  if (d > -1) return `Hạn đợt ${d + 1} phải sau đợt ${d}`;
  const max = planMaxDate(rows);
  if (dayjs(rows[rows.length - 1].dueDate).isAfter(max, 'day')) return `Đợt cuối phải trong ${MAX_PLAN_MONTHS} tháng (hạn chót ${max.format(DATE_FMT)})`;
  return null;
};

/** Chỉ ghi nhận đúng số tiền đợt 1: phần tiền mặt nhập vượt chuyển thành tiền thối (giữ nguyên "khách đưa") */
export const capPayments = (payments: PaymentLineInput[], cap: number): PaymentLineInput[] => {
  let excess = Math.max(0, payments.reduce((s, p) => s + p.amount, 0) - cap);
  return payments.map(p => {
    if (p.method !== 'cash' || excess <= 0) return p;
    const cut = Math.min(excess, p.amount);
    excess -= cut;
    return { ...p, amount: p.amount - cut };
  });
};


// ---------------------------------------------------------------- ô nhập

const fmtPct = (n: number) => String(Math.round(n * 10) / 10);

/** Ô nhập %: giữ chuỗi đang gõ (vd "33.") khi focus, hiện giá trị suy ra từ số tiền khi blur; ↑/↓ tăng giảm 1% (Shift: 5%) */
function PercentInput({ value, onChange }: { value: number; onChange: (pct: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const step = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const size = e.shiftKey ? 5 : 1;
    const cur = Number(draft ?? value) || 0;
    // Bước về số tròn: 33.3 ↑ → 34, 33.3 ↓ → 33
    const next = e.key === 'ArrowUp' ? Math.floor(cur / size + 1e-9) * size + size : Math.ceil(cur / size - 1e-9) * size - size;
    const pct = Math.max(0, Math.min(100, next));
    setDraft(fmtPct(pct));
    onChange(pct);
  };
  return (
    <div className="relative">
      <input
        type="text" inputMode="decimal" autoComplete="off" aria-label="Phần trăm" title="↑/↓ tăng giảm 1% · Shift: 5%"
        value={draft ?? fmtPct(value)}
        onKeyDown={step}
        onFocus={e => { setDraft(fmtPct(value)); e.target.select(); }}
        onBlur={() => setDraft(null)}
        onChange={e => {
          const s = e.target.value.replace(',', '.').replace(/[^\d.]/g, '').slice(0, 5);
          setDraft(s);
          const n = Number(s);
          if (s !== '' && !Number.isNaN(n)) onChange(Math.min(100, n));
        }}
        className="h-8 w-full rounded-lg border border-slate-300 bg-white pl-1.5 pr-5 text-right text-sm text-slate-700 outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20"
      />
      <span className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center text-xs text-slate-400">%</span>
    </div>
  );
}

const QUICK_STEPS: { label: string; n: number; unit: 'day' | 'month' }[] = [
  { label: '+2 tuần', n: 14, unit: 'day' },
  { label: '+1 tháng', n: 1, unit: 'month' },
  { label: '+2 tháng', n: 2, unit: 'month' },
  { label: '+3 tháng', n: 3, unit: 'month' },
];

/** Hạn một đợt: bấm mở bảng chọn nhanh (+2 tuần / +1 tháng…), gõ tắt hoặc chọn trên lịch */
function DueDateCell({ value, prev, maxDate, shiftNext, onShiftNext, onChange }: {
  value: string;
  prev: string;
  /** Hạn chót của kế hoạch (12 tháng kể từ đợt 1) */
  maxDate: Dayjs;
  shiftNext: boolean;
  onShiftNext: (v: boolean) => void;
  onChange: (d: Dayjs) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cur = dayjs(value);
  const prevDay = dayjs(prev);
  const tooLate = (d: Dayjs) => d.isAfter(maxDate, 'day');
  const raw = text ? parseDueInput(text, prevDay) : null;
  const parsed = raw && typeof raw !== 'string' && tooLate(raw) ? `Quá ${MAX_PLAN_MONTHS} tháng – hạn chót ${maxDate.format(DATE_FMT)}` : raw;

  const apply = (d: Dayjs) => {
    onChange(d);
    setOpen(false);
    setText('');
  };

  const content = (
    <div className="w-[272px] space-y-2">
      <div className="text-xs text-slate-500">Tính từ đợt trước: <b className="text-slate-700">{prevDay.format(DATE_FMT)}</b></div>
      <div className="grid grid-cols-4 gap-1">
        {QUICK_STEPS.map(q => {
          const d = prevDay.add(q.n, q.unit);
          const active = d.isSame(cur, 'day');
          const late = tooLate(d);
          return (
            <button key={q.label} onClick={() => apply(d)} disabled={late} title={late ? `Quá hạn chót ${maxDate.format(DATE_FMT)}` : d.format(DATE_FMT)}
              className={`h-8 rounded-md border text-xs font-semibold disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-300 ${active ? 'border-[#357f96] bg-[#357f96] text-white' : 'border-slate-300 text-slate-700 hover:border-[#3e91ab] hover:bg-[#ecf6f9]'}`}>
              {q.label}
            </button>
          );
        })}
      </div>
      <div>
        <input
          ref={inputRef} value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && parsed && typeof parsed !== 'string') apply(parsed); }}
          placeholder="Gõ: 15 · 15/12 · +45 · +2t · +3w"
          className="h-9 w-full rounded-lg border border-slate-300 px-2.5 text-sm outline-none focus:border-[#3e91ab] focus:ring-2 focus:ring-[#3e91ab]/20"
        />
        <div className="mt-1 h-4 text-xs">
          {typeof parsed === 'string' ? <span className="text-red-600">{parsed}</span>
            : parsed ? <span className="text-[#2c6b7f]">→ {WEEKDAY[parsed.day()]}, {parsed.format(DATE_FMT)} ({relLabel(prevDay, parsed)}) · Enter</span>
            : <span className="text-slate-400">Enter để áp dụng</span>}
        </div>
      </div>
      {showCalendar ? (
        <div className="rounded-lg border border-slate-200">
          <ConfigProvider locale={viVN}>
            <Calendar fullscreen={false} defaultValue={cur} disabledDate={d => !d.isAfter(prevDay, 'day') || tooLate(d)}
              onSelect={(d, info) => { if (info.source === 'date') apply(d); }}
              headerRender={({ value: v, onChange: go }) => (
                <div className="flex items-center justify-between px-2 py-1.5">
                  <button onClick={() => go(v.subtract(1, 'month'))} className="flex h-7 w-7 items-center justify-center rounded hover:bg-slate-100" aria-label="Tháng trước"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm font-semibold text-slate-700">Tháng {v.format('MM/YYYY')}</span>
                  <button onClick={() => go(v.add(1, 'month'))} className="flex h-7 w-7 items-center justify-center rounded hover:bg-slate-100" aria-label="Tháng sau"><ChevronRight className="h-4 w-4" /></button>
                </div>
              )} />
          </ConfigProvider>
        </div>
      ) : (
        <button onClick={() => setShowCalendar(true)} className="flex items-center gap-1 text-xs font-semibold text-[#2c6b7f]">
          <CalendarDays className="h-3.5 w-3.5" /> Chọn trên lịch
        </button>
      )}
      <Checkbox checked={shiftNext} onChange={e => onShiftNext(e.target.checked)} className="text-xs">Dời các đợt sau theo</Checkbox>
    </div>
  );

  return (
    <Popover trigger="click" placement="leftBottom" open={open} content={content} destroyOnHidden
      onOpenChange={o => { setOpen(o); if (!o) { setText(''); setShowCalendar(false); } }}
      afterOpenChange={o => { if (o) inputRef.current?.focus(); }}>
      <button className="flex h-8 w-full items-center justify-between gap-1 rounded-lg border border-slate-300 bg-white px-2 text-left text-sm text-slate-700 hover:border-[#3e91ab]">
        <span className="shrink-0">{cur.format('DD/MM/YY')}</span>
        <span className="flex min-w-0 items-center gap-0.5 truncate text-[11px] text-slate-400" >{relLabel(prevDay, cur)}<ChevronDown className="h-3 w-3 shrink-0" /></span>
      </button>
    </Popover>
  );
}

// ---------------------------------------------------------------- khung chia đợt

interface Props {
  total: number;
  rows: PlanRow[];
  onChange: (rows: PlanRow[]) => void;
  onRemove: () => void;
}

export default function InstallmentPlan({ total, rows, onChange, onRemove }: Props) {
  const last = rows.length - 1;
  const error = planError(rows, total);
  const [countSearch, setCountSearch] = useState('');
  const [shiftNext, setShiftNext] = useState(true);
  const cycle = detectCycle(rows);
  const from = dayjs(rows[0].dueDate);

  // Gõ số → hiện đúng số đó lên đầu (Enter để chọn), kèm các gợi ý bắt đầu bằng chữ số đã gõ
  const countOptions = useMemo(() => {
    const typed = Number(countSearch);
    const list = countSearch ? PRESET_COUNTS.filter(n => String(n).startsWith(countSearch) && n !== typed) : PRESET_COUNTS;
    const head = countSearch && Number.isInteger(typed) && typed >= 2 && typed <= MAX_INSTALLMENTS ? [typed] : [];
    const withCurrent = !countSearch && !PRESET_COUNTS.includes(rows.length) ? [...list, rows.length].sort((a, b) => a - b) : list;
    return [...head, ...withCurrent].map(n => ({ value: n, label: `${n} đợt` }));
  }, [countSearch, rows.length]);

  /** Đặt số tiền một đợt; phần chênh dồn vào đợt cuối (sửa đợt cuối thì dồn vào đợt liền trước) */
  const setAmount = (i: number, raw: number) => {
    const amount = Math.max(0, Math.min(raw, total));
    const absorber = i === last ? last - 1 : last;
    const next = rows.map((r, j) => (j === i ? { ...r, amount } : r));
    const others = next.reduce((s, r, j) => (j === absorber ? s : s + r.amount), 0);
    next[absorber] = { ...next[absorber], amount: total - others };
    onChange(next);
  };
  const setPercent = (i: number, pct: number) => setAmount(i, Math.round((total * pct) / 100 / UNIT) * UNIT);

  /** Đổi hạn một đợt; bật "dời các đợt sau" thì mỗi đợt sau giữ khoảng cách cũ với đợt liền trước (tròn tháng vẫn tròn tháng) */
  const setDate = (i: number, d: Dayjs) => {
    const next = rows.map((r, j) => (j === i ? { ...r, dueDate: d.format(ISO) } : r));
    if (shiftNext) {
      for (let j = i + 1; j < rows.length; j += 1) {
        const { k, unit } = gapOf(dayjs(rows[j - 1].dueDate), dayjs(rows[j].dueDate));
        next[j] = { ...next[j], dueDate: dayjs(next[j - 1].dueDate).add(k, unit).format(ISO) };
      }
    }
    onChange(next);
  };

  const applyCycle = (c: Cycle) => {
    const dates = datesFor(from, rows.length, c);
    onChange(rows.map((r, i) => ({ ...r, dueDate: dates[i] })));
  };

  return (
    <div className="rounded-lg border border-[#3e91ab]/30 bg-white p-2.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-semibold text-slate-700"><CalendarClock className="h-4 w-4 text-[#3e91ab]" /> Chia đợt thanh toán</span>
        <div className="flex items-center gap-1">
          <Select size="small" className="w-24" value={rows.length} popupMatchSelectWidth={false}
            showSearch searchValue={countSearch} filterOption={false} options={countOptions}
            onSearch={s => setCountSearch(s.replace(/\D/g, '').slice(0, 2))}
            onChange={n => { setCountSearch(''); onChange(splitPlan(total, n, from, cycle || DEFAULT_CYCLE)); }}
            onBlur={() => setCountSearch('')}
            notFoundContent={<span className="text-xs">Nhập 2–{MAX_INSTALLMENTS}</span>} />
          <button onClick={() => onChange(splitPlan(total, rows.length, from, cycle || DEFAULT_CYCLE))} className="h-6 rounded px-1.5 text-xs font-semibold text-[#2c6b7f] hover:bg-[#ecf6f9]">Chia đều</button>
          <button onClick={onRemove} className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500" aria-label="Bỏ chia đợt"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-1 text-xs">
        <span className="mr-1 text-slate-500">Mỗi đợt cách</span>
        {CYCLES.map(c => (
          <button key={c.label} onClick={() => applyCycle(c)}
            className={`h-6 rounded-full border px-2.5 font-semibold ${cycle === c ? 'border-[#357f96] bg-[#357f96] text-white' : 'border-slate-300 text-slate-600 hover:border-[#3e91ab] hover:bg-[#ecf6f9]'}`}>
            {c.label}
          </button>
        ))}
        {!cycle && <span className="ml-1 text-slate-400">· tuỳ chỉnh</span>}
      </div>
      <div className="pos-scroll max-h-44 space-y-1.5 overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[44px_minmax(0,168px)_minmax(110px,1fr)_60px] items-center gap-1.5">
            <span className={`text-xs font-semibold ${i === 0 ? 'text-[#2c6b7f]' : 'text-slate-600'}`}>Đợt {i + 1}</span>
            {i === 0 ? (
              <span className="text-xs text-slate-500">Thu ngay hôm nay</span>
            ) : (
              <DueDateCell value={r.dueDate} prev={rows[i - 1].dueDate} maxDate={planMaxDate(rows)} shiftNext={shiftNext} onShiftNext={setShiftNext} onChange={d => setDate(i, d)} />
            )}
            <MoneyInput value={r.amount} onChange={v => setAmount(i, v)} invalid={r.amount <= 0} className="!h-8 !text-sm" />
            <PercentInput value={total > 0 ? (r.amount / total) * 100 : 0} onChange={p => setPercent(i, p)} />
          </div>
        ))}
      </div>
      {error ? (
        <p className="mt-1.5 text-xs font-medium text-red-600">{error}</p>
      ) : planNeedsApproval(rows, total) ? (
        <p className="mt-1.5 text-xs font-medium text-amber-700">Đợt 1 chỉ {fmtPct(firstPercent(rows, total))}% – dưới {MIN_FIRST_PCT}% cần Quản lý duyệt</p>
      ) : (
        <p className="mt-1.5 text-[11px] text-slate-400">Tối đa {MAX_INSTALLMENTS} đợt trong {MAX_PLAN_MONTHS} tháng · đợt 1 ≥ {MIN_FIRST_PCT}% tổng đơn</p>
      )}
    </div>
  );
}
